import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks/hooks";
import { update } from "../../features/formDataSlice"; 
import { Form } from "react-bootstrap";
import TagManager from "react-gtm-module";
import { v4 as uuidv4 } from "uuid";
import errors from "../../utils/errors"

import "./TransferYourPhone.css";

// Accessing kubernetes deployment constants from config.json
import ConfigContext from '../../context/configContext';

import { FormDataContext } from "./FormDataContext";
import { EligibilityComponent } from "./components/EligibilityComponent";
import { PersonalDataForm } from "./components/PersonalDataForm";
import { HeaderSection } from "./components/HeaderSection";
import { US_PHONE_LEN } from "../../utils/phone-utils";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PortingForm, portingFormSchema } from "../../schema/porting";

const TransferYourPhonePage = () => {
  const config = useContext(ConfigContext);

  const [canContinue, setCanContinue] = useState<boolean>(false);

  let formState = useAppSelector((state) => state.formData.value);
  const dispatch = useAppDispatch();

      const methods = useForm<PortingForm>({
      resolver: zodResolver(portingFormSchema),
      defaultValues: {
        eligibilityNumber: formState.eligibilityNumber ?? "",
        otherProvider: formState.otherProvider ?? "",
        carrier: formState.provider ?? "",
        firstName: formState.firstName ?? "",
        lastName: formState.lastName ?? "",
        billingAddress: formState.billingAddress ?? "",
        billingAddress2: formState.billingAddress2 ?? "",
        city: formState.city ?? "",
        state: formState.state ?? "",
        zip: formState.zip ?? "",
        accountNumber: formState.accountNumber ?? '',
        accountPIN: formState.accountPIN ?? '',
      },
    });

  const [isEligible, setIsEligible] = useState(false);
  const [
    shouldEligibleNotificationAppear,
    setShouldEligibleNotificationAppear,
  ] = useState(false);
  const [
    shouldNotEligibleNotificationAppear,
    setShouldNotEligibleNotificationAppear,
  ] = useState(false);
  const [isFormFillable, setIsFormFillable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formContext, setFormContext] = useState({});
  const [apiStatus, setApiStatus] = useState(true);
  const [eligibiltyTimeoutId, setEligibiltyTimeoutId] = useState< string | number | NodeJS.Timeout | undefined>(undefined);
  const [showActiveLivelyNumberError, setShowActiveLivelyNumberError] = useState(false);

  const checkEligibility = async (e: { preventDefault: () => void }) => {
    // Set the isLoading state to true when the API request starts
    setIsLoading(true);
    setShouldNotEligibleNotificationAppear(false);
    
    TagManager.dataLayer({
      dataLayer: {
        event: "form_start",
        form_name: "Porting"
      },
    });

    const headers = new Headers();
    headers.append("X-REQUEST-ID", formState.uuid);

    const requestOptions = {
      method: "GET",
      headers: headers,
    };

    e.preventDefault();
    let phoneNumber = formState.eligibilityNumber.replace(/\D/g, "");

    try {
      const response = (await Promise.race([
        fetch(
          `${config.ACCT_MGT_PORTING_BACKEND}/myaccount/api/porting/number-eligibility?phoneNumber=${phoneNumber}`,
          requestOptions
        ),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("API timed out")), 60000)
        ),
      ])) as Response;

      const data = await response.json();
      const { isEligible, reason } = data;

      setApiStatus(true);
      
      if (response.status === 401) throw "Missing Token";

      if (isEligible) {
        TagManager.dataLayer({
          dataLayer: {
            event: "porting_event",
            event_name: "successful_eligibility_check",
            form_name: "Porting",
            form_step: "check eligibility",
            form_step_action: "submit",
          },
        });

        setShouldEligibleNotificationAppear(true);
        setEligibiltyTimeoutId(
          setTimeout(() => {
            showQuestions();
          }, 5000)
        );
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: "porting_event",
            event_name: "unsuccessful_eligibility_check",
            form_name: "Porting",
            form_step: "check eligibility",
            form_step_action: "submit",
          },
        });

        if (reason && reason === errors[100]) {
          setShowActiveLivelyNumberError(true);
        } else {
          setShouldNotEligibleNotificationAppear(true);
        }
      }
    } catch (error: unknown) {
      setIsLoading(false);
      console.error("Error fetching data:", error);

      switch (error) {
        case 'Missing Token':
          console.error(error);
          setApiStatus(false);
          break;
          
        default: setApiStatus(false);
      }

      setTimeout(()=>{
              window.location.href =
        `${config.ACCT_MGT_PORTING_FRONTEND}/myaccount/accountoverview?portingcallback=true`;
      }, 7000)
    }
  };

  const showQuestions = () => {
    setIsEligible(true);
    setIsLoading(false);
  };

  const updateEligibiltyTimeout = () => {
    setIsLoading(false);
    if (eligibiltyTimeoutId) {
      clearTimeout(eligibiltyTimeoutId);
      setEligibiltyTimeoutId(undefined);
    }
  }

  const navigate = useNavigate();

  const handleContinueOnClick = () => {
    if (!canContinue) return;
    const formData = JSON.stringify(formState);
    navigate(`/review-phone-number`);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    dispatch(update({ ...formState, uuid: uuidv4() }));

    const serviceId = !sessionStorage.getItem("serviceID") ? formState.serviceId : sessionStorage.getItem("serviceID")
    const customerId = !sessionStorage.getItem("customerID") ? formState.customerId : sessionStorage.getItem("customerID")
    const deviceType = !sessionStorage.getItem("deviceType") ? formState.deviceType : sessionStorage.getItem("deviceType")


    if(serviceId && customerId && deviceType) {
      formState = dispatch(update({...formState, serviceId, customerId, deviceType})).payload;
      sessionStorage.removeItem("customerID");
      sessionStorage.removeItem("serviceID");
      sessionStorage.removeItem("deviceType");
    }
  }, []);
  
  useEffect(() => {
    const customerId = formState.customerId;
    const serviceId = formState.serviceId;
    
    if (!customerId || !serviceId) {
      // comment out the following line during dev fast repro

      TagManager.dataLayer({
        dataLayer: {
          event: "porting_event",
          event_name: "no_user_in_session",
          form_name: "Porting",
          form_step: "check eligibility",
          form_step_action: "none",
        },
      });

      window.location.href =
        `${config.ACCT_MGT_PORTING_FRONTEND}/myaccount/accountoverview?portingcallback=true`;
    }

    // if the form already has saved info, continue filling out the form
    // eslint-disable-next-line
    if (
      formState.eligibilityNumber.replace(/[\(\)\s-]/g, "").length ===
        US_PHONE_LEN &&
      formState.landline !== ""
    ) {
      setIsEligible(true);
    }
  }, [
    formState.eligibilityNumber,
    formState.eligibilityNumber.length,
    formState.landline,
  ]);

  useEffect(() => {
    const mandatoryFields = [
      "firstName",
      "lastName",
      "billingAddress",
      "city",
      "state",
      "zip",
      "accountNumber",
      "accountPIN",
      "landline",
    ];

    let error = false;
    for (const key of mandatoryFields) {
      if (formState[key]?.length === 0) error = true;
    }
    setCanContinue(!error);
  }, [formState]);

  return (
    <FormDataContext.Provider value={{ formContext, setFormContext }}>
      <div className="transfer-phone-container">
        <div className="transfer-phone-content">
          <HeaderSection />
          <FormProvider {...methods}>
            <Form
            //form submission happens on personal data form continue button click
              onSubmit={methods.handleSubmit(() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: "porting_event",
                    event_name: "personal_data_finish",
                    form_name: "Porting",
                    form_step: "personal data",
                    form_step_action: "Continue",
                  },
                });
                handleContinueOnClick();
              })}
            >
              <EligibilityComponent
                setIsEligible={setIsEligible}
                isEligible={isEligible}
                showQuestions={showQuestions}
                setIsFormFillable={setIsFormFillable}
                isFormFillable={isFormFillable}
                isLoading={isLoading}
                checkEligibility={checkEligibility}
                apiStatus={apiStatus}
                shouldEligibleNotificationAppear={shouldEligibleNotificationAppear}
                setShouldEligibleNotificationAppear={setShouldEligibleNotificationAppear}
                shouldNotEligibleNotificationAppear={shouldNotEligibleNotificationAppear}
                setShouldNotEligibleNotificationAppear={setShouldNotEligibleNotificationAppear}
                updateEligibiltyTimeout={updateEligibiltyTimeout}
                showActiveLivelyNumberError={showActiveLivelyNumberError}
                setShowActiveLivelyNumberError={setShowActiveLivelyNumberError}
              />
              {formState.primaryAccountHolder !== "" &&
                isEligible &&
                isFormFillable && (
                  <PersonalDataForm
                    canContinue={canContinue && methods.formState.isValid}
                  />
                )}
            </Form>
          </FormProvider>
        </div>
      </div>
    </FormDataContext.Provider>
  );
};

export default TransferYourPhonePage;
